/**
 * Multi-Step Inequalities generator.
 *
 * Linear inequalities requiring distribution / combining like terms before
 * isolating the variable, deliberately including cases where dividing by a
 * negative flips the sign. The solution is computed and verified by the
 * independent LinearInequality solver (and boundary probes), so the flip rule
 * can never be emitted incorrectly.
 *
 * Families:
 *   Q1: ax + b (op) c
 *   Q2: a(x + b) (op) c
 *   Q3: ax + b (op) cx + d
 *   Q4: a(x + b) + c (op) d
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr } from "../engine/linear";
import {
  LinearInequality,
  solutionSetToAnswer,
  solutionSetLatex,
  type Op,
  type SolutionSet,
} from "../engine/inequality";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
  SolutionStep,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "multi-step-inequalities" as const;
const VERSION = "1.0.0";
const VARS = ["x", "n", "a", "m", "p", "y"] as const;
const OPS: readonly Op[] = ["<", "<=", ">", ">="];

type Family = "Q1" | "Q2" | "Q3" | "Q4";

interface Cfg {
  readonly families: readonly Family[];
  readonly cMin: number;
  readonly cMax: number;
  readonly kMax: number;
  readonly bMin: number;
  readonly bMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["Q1"], cMin: 1, cMax: 5, kMax: 10, bMin: -8, bMax: 8, pNeg: 0.2 },
  standard: { families: ["Q1", "Q2", "Q3"], cMin: 1, cMax: 7, kMax: 12, bMin: -10, bMax: 10, pNeg: 0.45 },
  hard: { families: ["Q2", "Q3", "Q4"], cMin: 2, cMax: 9, kMax: 14, bMin: -12, bMax: 12, pNeg: 0.5 },
  challenge: { families: ["Q3", "Q4"], cMin: 2, cMax: 11, kMax: 16, bMin: -12, bMax: 12, pNeg: 0.55 },
};

function factorL(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toLatex();
}
function coeffL(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
function opL(op: Op): string {
  return op === "<=" ? "\\le" : op === ">=" ? "\\ge" : op;
}

interface Built {
  family: Family;
  variable: string;
  displayLatex: string;
  ineq: LinearInequality;
  set: SolutionSet;
}

function build(rng: Rng, difficulty: Difficulty): Built | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(VARS);
  const family = rng.pick(cfg.families);
  const op = rng.pick(OPS);
  const c = () => rat(rng.int(cfg.cMin, cfg.cMax) * rng.sign(cfg.pNeg));
  const k = () => rat(rng.int(1, cfg.kMax) * rng.sign(cfg.pNeg));

  let left: LinearExpr;
  let right: LinearExpr;
  let displayLatex: string;

  switch (family) {
    case "Q1": {
      const a = c();
      if (a.isZero()) return null;
      const b = k();
      const rhs = k();
      left = new LinearExpr(a, b);
      right = LinearExpr.constantExpr(rhs);
      displayLatex = `${coeffL(a, v)} ${plusL(b)} ${opL(op)} ${rhs.toLatex()}`;
      break;
    }
    case "Q2": {
      const a = c();
      if (a.isZero()) return null;
      const b = k();
      const rhs = k();
      left = LinearExpr.variable().add(LinearExpr.constantExpr(b)).scale(a);
      right = LinearExpr.constantExpr(rhs);
      displayLatex = `${factorL(a)}(${v} ${plusL(b)}) ${opL(op)} ${rhs.toLatex()}`;
      break;
    }
    case "Q3": {
      const a = c();
      let cc = c();
      if (a.equals(cc)) cc = cc.add(rat(1)); // avoid vanishing variable
      const b = k();
      const d = k();
      left = new LinearExpr(a, b);
      right = new LinearExpr(cc, d);
      displayLatex = `${coeffL(a, v)} ${plusL(b)} ${opL(op)} ${coeffL(cc, v)} ${plusL(d)}`;
      break;
    }
    case "Q4": {
      const a = c();
      if (a.isZero()) return null;
      const b = k();
      const cc = k();
      const rhs = k();
      left = LinearExpr.variable().add(LinearExpr.constantExpr(b)).scale(a).add(LinearExpr.constantExpr(cc));
      right = LinearExpr.constantExpr(rhs);
      displayLatex = `${factorL(a)}(${v} ${plusL(b)}) ${plusL(cc)} ${opL(op)} ${rhs.toLatex()}`;
      break;
    }
  }

  const ineq = new LinearInequality(left, op, right);
  const solved = ineq.solve();
  if (solved.kind !== "ray") return null; // avoid all/none for this topic
  const set: SolutionSet = { kind: "ray", op: solved.op, value: solved.value };
  return { family, variable: v, displayLatex, ineq, set };
}

/** Independently verify the ray by probing interior/exterior points. */
function verify(b: Built): boolean {
  if (b.set.kind !== "ray") return false;
  const value = b.set.value;
  const interior =
    b.set.op === "<" || b.set.op === "<=" ? value.sub(rat(1)) : value.add(rat(1));
  const exterior =
    b.set.op === "<" || b.set.op === "<=" ? value.add(rat(1)) : value.sub(rat(1));
  if (!b.ineq.isSatisfiedBy(interior)) return false;
  if (b.ineq.isSatisfiedBy(exterior)) return false;
  // Boundary inclusion matches strictness.
  const boundaryHolds = b.ineq.isSatisfiedBy(value);
  const inclusive = b.set.op === "<=" || b.set.op === ">=";
  return boundaryHolds === inclusive;
}

function steps(b: Built, v: string): SolutionStep[] {
  const flipped = didFlip(b);
  const s: SolutionStep[] = [
    {
      description: "Simplify each side, then isolate the variable term.",
      latex: b.displayLatex,
    },
  ];
  if (flipped) {
    s.push({
      description:
        "You divided (or multiplied) by a negative, so reverse the inequality sign.",
      latex: solutionSetLatex(b.set, v),
    });
  } else {
    s.push({
      description: "Divide by the coefficient to isolate the variable.",
      latex: solutionSetLatex(b.set, v),
    });
  }
  return s;
}

/** Did solving require a flip? The variable coefficient after moving is negative. */
function didFlip(b: Built): boolean {
  const a = b.ineq.left.coeff.sub(b.ineq.right.coeff);
  return a.isNegative();
}

export class MultiStepInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 200; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const b = build(rng, ctx.difficulty);
      if (!b) continue;
      if (!verify(b)) continue;
      const answer = solutionSetToAnswer(b.set);

      return {
        id: `${TOPIC}-${ctx.seed}-${b.family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: b.family,
        difficulty: ctx.difficulty,
        prompt: { text: b.displayLatex, latex: b.displayLatex },
        answerType: "inequality",
        answer,
        steps: steps(b, b.variable),
        hints: [
          { level: 1, text: "Solve it like an equation to isolate the variable." },
          { level: 2, text: "If you divide or multiply by a negative, flip the inequality sign." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 200 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const a = q.answer;
    const structure =
      a.kind === "inequality" ? `${a.op}${a.value.key()}` : a.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const multiStepInequalities = new MultiStepInequalitiesGenerator();
